/**
 * JsBridge 核心封装
 * 提供 iOS / Android / HarmonyOS WebView 通信的基础能力
 *
 * 三端 Bridge 机制说明：
 *
 * ┌─────────────┬──────────────────────────────────────────────────────────────┐
 * │ iOS         │ WVJBCallbacks 机制：H5 将回调推入 window.WVJBCallbacks，      │
 * │             │ Native 注入 JS 后遍历执行所有回调，传入 Bridge 实例。          │
 * │             │ 触发方式：创建隐藏 iframe，src = https://__bridge_loaded__    │
 * ├─────────────┼──────────────────────────────────────────────────────────────┤
 * │ Android     │ 事件机制：监听 document 上的 WebViewJavascriptBridgeReady     │
 * │             │ 事件，Native 注入完成后触发该事件，H5 在回调中拿到 Bridge。    │
 * │             │ 需要调用 jsbridge.init() 完成握手。                           │
 * ├─────────────┼──────────────────────────────────────────────────────────────┤
 * │ HarmonyOS   │ 与 iOS 相同的 WVJBCallbacks 机制（@yue/webview_javascript_   │
 * │             │ bridge 库的 H5 侧注入逻辑与 iOS WVJBCallbacks 完全一致）。    │
 * │             │ 触发方式：创建隐藏 iframe，src = https://__bridge_loaded__    │
 * │             │ 不需要调用 jsbridge.init()，无需额外握手。                    │
 * └─────────────┴──────────────────────────────────────────────────────────────┘
 *
 * UA 特征：
 *   iOS       — 含 iPhone / iPad / iPod / iOS
 *   Android   — 含 Android（且不含 ArkWeb）
 *   HarmonyOS — 含 ArkWeb（华为鸿蒙 WebView 标识）
 */

const u = navigator.userAgent

/** 是否 iOS 设备 */
const isIOS = /(iPhone|iPad|iPod|iOS)/i.test(u)
/** 是否 HarmonyOS 设备（ArkWeb 为鸿蒙 WebView 的 UA 特征） */
const isHarmony = /ArkWeb/i.test(u)
/** 是否 Android 设备（排除鸿蒙，避免部分鸿蒙设备 UA 同时含 Android） */
const isAndroid = /Android/i.test(u) && !isHarmony

// ==================== iOS Bridge ====================

/**
 * iOS Bridge 初始化函数
 *
 * 原理：
 * 1. 若 window.WebViewJavascriptBridge 已存在（Native 已注入），直接执行回调
 * 2. 若 window.WVJBCallbacks 已存在（其他地方已触发过初始化），将回调追加到队列
 * 3. 否则创建队列并触发初始化：创建隐藏 iframe，src 设为 https://__bridge_loaded__，
 *    Native 拦截该 URL 后注入 Bridge JS，注入完成后遍历执行 WVJBCallbacks 中的所有回调
 */
function setupWebViewJavascriptBridge(callback: (bridge: any) => void): void {
  if (window.WebViewJavascriptBridge) {
    return callback(window.WebViewJavascriptBridge)
  }
  if (window.WVJBCallbacks) {
    return window.WVJBCallbacks.push(callback)
  }
  window.WVJBCallbacks = [callback]
  const WVJBIframe = document.createElement('iframe')
  WVJBIframe.style.display = 'none'
  WVJBIframe.src = 'https://__bridge_loaded__'
  document.documentElement.appendChild(WVJBIframe)
  setTimeout(() => {
    document.documentElement.removeChild(WVJBIframe)
  }, 0)
}

// iOS Bridge 初始化（页面加载时触发，确保 Bridge 尽早就绪）
if (isIOS) {
  setupWebViewJavascriptBridge(function (_jsbridge) {
    console.log('[JsBridge] iOS Bridge 初始化成功')
  })
}

// ==================== Android Bridge ====================

/**
 * Android Bridge 初始化函数
 *
 * 原理：
 * 1. 若 window.WebViewJavascriptBridge 已存在（Native 已注入），直接执行回调
 * 2. 否则监听 document 上的 WebViewJavascriptBridgeReady 事件，
 *    Native 注入 Bridge JS 完成后会触发该事件，H5 在回调中拿到 Bridge 实例
 */
function connectWebViewJavascriptBridge(callback: (bridge: any) => void): void {
  if (window.WebViewJavascriptBridge) {
    callback(window.WebViewJavascriptBridge)
  } else {
    document.addEventListener(
      'WebViewJavascriptBridgeReady',
      () => {
        callback(window.WebViewJavascriptBridge)
      },
      false,
    )
  }
}

// Android Bridge 初始化（需调用 jsbridge.init() 完成握手）
if (isAndroid) {
  connectWebViewJavascriptBridge(function (jsbridge) {
    console.log('[JsBridge] Android Bridge 初始化成功')
    jsbridge.init(function (message: any, responseCallback: (data: any) => void) {
      console.log('[JsBridge] Android init 回调被调用, message:', message)
      responseCallback({ 'Javascript Responds': 'Android调用JS初始化方法!' })
    })
  })
}

// ==================== HarmonyOS Bridge ====================

/**
 * HarmonyOS Bridge 初始化函数
 *
 * @yue/webview_javascript_bridge 的 H5 侧注入机制与 iOS WVJBCallbacks 完全相同：
 * - 同样使用 window.WVJBCallbacks 数组作为就绪回调队列
 * - 同样通过 iframe src = https://__bridge_loaded__ 触发 Native 拦截并注入 Bridge JS
 * - 不需要调用 jsbridge.init()，无需额外握手
 *
 * 因此直接复用 setupWebViewJavascriptBridge，但作为独立分支保持逻辑清晰。
 */
function setupHarmonyBridge(callback: (bridge: any) => void): void {
  // 与 iOS 机制完全相同，复用同一套 WVJBCallbacks 初始化逻辑
  setupWebViewJavascriptBridge(callback)
}

// HarmonyOS Bridge 初始化
if (isHarmony) {
  setupHarmonyBridge(function (_jsbridge) {
    console.log('[JsBridge] HarmonyOS Bridge 初始化成功')
  })
}

// ==================== 导出 ====================

export default {
  /**
   * 注册供 Native 调用的 Handler
   * @param name Handler 名称（需与 Native 侧一致）
   * @param fun  处理函数，接收 data 和可选的 responseCallback
   */
  registerHandler: function (name: string, fun: (data: any, responseCallback?: any) => any) {
    console.log(`[JsBridge] 注册 Handler: ${name}`)
    if (isIOS) {
      // iOS：通过 WVJBCallbacks 机制注册
      setupWebViewJavascriptBridge(function (jsbridge) {
        jsbridge.registerHandler(name, function (data: any, responseCallback: (res: any) => void) {
          console.log(`[JsBridge] iOS Handler 被调用: ${name}, data:`, data)
          const result = fun(data, responseCallback)
          if (typeof responseCallback === 'function' && result !== undefined) {
            responseCallback(result)
          }
        })
      })
    } else if (isHarmony) {
      // HarmonyOS：通过 WVJBCallbacks 机制注册（与 iOS 相同，独立分支便于后续差异化）
      setupHarmonyBridge(function (jsbridge) {
        jsbridge.registerHandler(name, function (data: any, responseCallback: (res: any) => void) {
          console.log(`[JsBridge] HarmonyOS Handler 被调用: ${name}, data:`, data)
          const result = fun(data, responseCallback)
          if (typeof responseCallback === 'function' && result !== undefined) {
            responseCallback(result)
          }
        })
      })
    } else {
      // Android：通过 WebViewJavascriptBridgeReady 事件机制注册
      connectWebViewJavascriptBridge(function (jsbridge) {
        jsbridge.registerHandler(name, function (data: any, responseCallback: (res: any) => void) {
          console.log(`[JsBridge] Android Handler 被调用: ${name}, data:`, data)
          const result = fun(data, responseCallback)
          if (typeof responseCallback === 'function' && result !== undefined) {
            responseCallback(result)
          }
        })
      })
    }
  },

  /**
   * 调用 Native 方法
   * @param name Handler 名称（需与 Native 侧一致）
   * @param data 传递给 Native 的数据
   * @param fun  Native 响应后的回调函数（可选）
   */
  callHandler: function (name: string, data: any, fun?: (data: any) => void) {
    console.log(`[JsBridge] 调用 Native 方法: ${name}, 参数:`, data)
    if (isIOS) {
      // iOS：通过 WVJBCallbacks 机制调用
      setupWebViewJavascriptBridge(function (jsbridge) {
        console.log(`[JsBridge] iOS 准备调用: ${name}`)
        jsbridge.callHandler(name, data, function (responseData: any) {
          console.log(`[JsBridge] iOS 收到响应: ${name}, 数据:`, responseData)
          fun && fun(responseData)
        })
      })
    } else if (isHarmony) {
      // HarmonyOS：通过 WVJBCallbacks 机制调用（与 iOS 相同，独立分支便于后续差异化）
      setupHarmonyBridge(function (jsbridge) {
        console.log(`[JsBridge] HarmonyOS 准备调用: ${name}`)
        jsbridge.callHandler(name, data, function (responseData: any) {
          console.log(`[JsBridge] HarmonyOS 收到响应: ${name}, 数据:`, responseData)
          fun && fun(responseData)
        })
      })
    } else {
      // Android：通过 WebViewJavascriptBridgeReady 事件机制调用
      connectWebViewJavascriptBridge(function (jsbridge) {
        console.log(`[JsBridge] Android 准备调用: ${name}`)
        jsbridge.callHandler(name, data, function (responseData: any) {
          console.log(`[JsBridge] Android 收到响应: ${name}, 数据:`, responseData)
          fun && fun(responseData)
        })
      })
    }
  },
}
